import { useEffect, useMemo, useState } from "react";
import { AsYouType, getCountries, getCountryCallingCode, isValidPhoneNumber, type CountryCode, type MetadataJson } from "libphonenumber-js/core";
import { Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/utils/functions/cn";
import type { Lang } from "@/utils/i18n";

export interface CountryOption {
  code: CountryCode;
  name: string;
  /** English name — what the backend stores, so the admin reads one language. */
  englishName: string;
  dial: string;
  flag: string;
}

// The phone metadata (~20 KB gzip) is fetched only when a phone field mounts, so it
// stays out of the bundle every other page loads. Until it arrives the helpers
// degrade to plain digits and the country list is empty.
let metadata: MetadataJson | null = null;
let metadataPromise: Promise<MetadataJson> | null = null;

function loadMetadata() {
  metadataPromise ??= import("libphonenumber-js/metadata.min.json").then((m) => (metadata = (m.default ?? m) as MetadataJson));
  return metadataPromise;
}

/** Loads the phone metadata once; re-renders the caller when it is ready. */
export function usePhoneMetadata() {
  const [ready, setReady] = useState(metadata !== null);
  useEffect(() => {
    if (!ready) loadMetadata().then(() => setReady(true));
  }, [ready]);
  return ready;
}

const fold = (v: string) => v.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const flagOf = (code: string) =>
  String.fromCodePoint(...code.toUpperCase().split("").map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));

function displayNames(lang: string) {
  try {
    return new Intl.DisplayNames([lang], { type: "region" });
  } catch {
    return null;
  }
}

/** Every country libphonenumber knows, named in the site language and sorted by that name. */
export function useCountries(lang: Lang): CountryOption[] {
  const ready = usePhoneMetadata();
  return useMemo(() => {
    if (!ready || !metadata) return [];
    const meta = metadata;
    const local = displayNames(lang);
    const en = displayNames("en");
    return getCountries(meta)
      .map((code) => ({
        code,
        name: local?.of(code) ?? code,
        englishName: en?.of(code) ?? code,
        dial: `+${getCountryCallingCode(code, meta)}`,
        flag: flagOf(code),
      }))
      .sort((a, b) => a.name.localeCompare(b.name, lang));
  }, [lang, ready]);
}

/** Country implied by the site language — the same on the server prerender and the client. */
export function defaultCountry(lang: Lang): CountryCode {
  const fromLang: Record<Lang, CountryCode> = { pt: "BR", en: "US", es: "ES", de: "DE", fr: "FR", uk: "UA" };
  return fromLang[lang];
}

/** Region of the browser locale ("pt-BR" -> "BR"), read after hydration. */
export function browserRegion(): CountryCode | undefined {
  const region = (typeof navigator !== "undefined" ? navigator.language : "").split("-")[1]?.toUpperCase();
  return region && /^[A-Z]{2}$/.test(region) ? (region as CountryCode) : undefined;
}

const panelClass =
  "w-[var(--radix-popover-trigger-width)] min-w-[260px] p-0 rounded-[12px] border border-white/[0.1] bg-[#161624] text-white shadow-2xl";

function CountryList({
  countries,
  value,
  onSelect,
  searchPlaceholder,
  emptyText,
  showDial,
}: {
  countries: CountryOption[];
  value?: CountryCode;
  onSelect: (c: CountryOption) => void;
  searchPlaceholder: string;
  emptyText: string;
  showDial: boolean;
}) {
  return (
    <Command
      className="bg-transparent text-white"
      // Every typed word must appear in the localized name, English name, ISO code or dial code
      // (accents ignored), so "bra", "+55", "estados unidos" and "us" all work.
      filter={(itemValue, search) => {
        const hay = fold(itemValue);
        return fold(search).replace(/\+/g, " ").split(/\s+/).filter(Boolean).every((w) => hay.includes(w)) ? 1 : 0;
      }}
    >
      <CommandInput placeholder={searchPlaceholder} className="h-11 text-sm text-white placeholder:text-white/35" />
      <CommandList className="max-h-[260px]">
        <CommandEmpty className="py-6 text-center text-sm text-white/50">{emptyText}</CommandEmpty>
        {countries.map((c) => (
          <CommandItem
            key={c.code}
            value={`${c.name} ${c.englishName} ${c.code} ${c.dial.slice(1)}`}
            onSelect={() => onSelect(c)}
            className="mx-1 flex cursor-pointer items-center gap-2.5 rounded-[8px] px-2.5 py-2 text-sm text-white/80 data-[selected=true]:bg-brand-accent/15 data-[selected=true]:text-white aria-selected:bg-brand-accent/15 aria-selected:text-white"
          >
            <span className="text-base leading-none">{c.flag}</span>
            <span className="flex-1 truncate">{c.name}</span>
            {showDial && <span className="text-xs tabular-nums text-white/45">{c.dial}</span>}
            {c.code === value && <Check className="h-4 w-4 text-brand-accent" />}
          </CommandItem>
        ))}
      </CommandList>
    </Command>
  );
}

/** Searchable country dropdown (type to filter). */
export function CountrySelect({
  countries,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  className,
}: {
  countries: CountryOption[];
  value?: CountryCode;
  onChange: (c: CountryOption) => void;
  placeholder: string;
  searchPlaceholder: string;
  emptyText: string;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = countries.find((c) => c.code === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-expanded={open}
          className={cn("flex w-full items-center gap-2 border px-3 text-left text-sm", className)}
        >
          {selected ? (
            <>
              <span className="text-base leading-none">{selected.flag}</span>
              <span className="flex-1 truncate text-white">{selected.name}</span>
            </>
          ) : (
            <span className="flex-1 truncate text-white/20">{placeholder}</span>
          )}
          <ChevronsUpDown className="h-4 w-4 shrink-0 text-white/40" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className={panelClass}>
        <CountryList
          countries={countries}
          value={value}
          searchPlaceholder={searchPlaceholder}
          emptyText={emptyText}
          showDial={false}
          onSelect={(c) => {
            onChange(c);
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Mask the digits typed after the dial code. National formatting only kicks in for
 * some countries once the trunk "0" is typed (UA, DE, FR...), so without it the
 * number is grouped as it is written internationally, minus the "+DDI" part.
 */
export function formatNational(digits: string, country: CountryCode) {
  if (!digits || !metadata) return digits;
  if (digits.startsWith("0")) return new AsYouType(country, metadata).input(digits);
  const cc = getCountryCallingCode(country, metadata);
  return new AsYouType(undefined, metadata).input(`+${cc}${digits}`).replace(new RegExp(`^\\+${cc}\\s?`), "");
}

/** E.164-ish "+55 11 99999-9999" for storage, or "" when empty. */
export function toInternational(national: string, country: CountryCode) {
  const digits = national.replace(/\D/g, "");
  if (!digits || !metadata) return digits;
  const typer = new AsYouType(country, metadata);
  typer.input(digits);
  return typer.getNumber()?.formatInternational() ?? `+${getCountryCallingCode(country, metadata)} ${digits}`;
}

export function isPhoneValid(national: string, country: CountryCode) {
  const digits = national.replace(/\D/g, "");
  return !digits || !metadata || isValidPhoneNumber(digits, country, metadata);
}

/** Phone input with a searchable dial-code (DDI) picker and a per-country mask. */
export function PhoneInput({
  countries,
  country,
  onCountryChange,
  value,
  onChange,
  invalid,
  onBlur,
  dialLabel,
  searchPlaceholder,
  emptyText,
  inputClassName,
}: {
  countries: CountryOption[];
  country: CountryCode;
  onCountryChange: (c: CountryOption) => void;
  value: string;
  onChange: (national: string) => void;
  invalid?: boolean;
  onBlur?: () => void;
  dialLabel: string;
  searchPlaceholder: string;
  emptyText: string;
  inputClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const selected = countries.find((c) => c.code === country);
  const ready = usePhoneMetadata();
  const example = useMemo(() => formatNational("9".repeat(11), country).replace(/9/g, "0"), [country, ready]);

  return (
    <div className={cn("flex items-stretch overflow-hidden border", inputClassName, invalid && "border-red-400/70")}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={`${dialLabel}: ${selected?.name ?? country} ${selected?.dial ?? ""}`}
            aria-expanded={open}
            className="flex shrink-0 items-center gap-1.5 border-r border-white/[0.08] pl-3 pr-2 text-sm text-white hover:bg-white/[0.04]"
          >
            <span className="text-base leading-none">{selected?.flag ?? flagOf(country)}</span>
            <span className="tabular-nums text-white/80">{selected?.dial ?? "+"}</span>
            <ChevronsUpDown className="h-3.5 w-3.5 text-white/40" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className={panelClass}>
          <CountryList
            countries={countries}
            value={country}
            searchPlaceholder={searchPlaceholder}
            emptyText={emptyText}
            showDial
            onSelect={(c) => {
              onCountryChange(c);
              setOpen(false);
            }}
          />
        </PopoverContent>
      </Popover>
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        value={value}
        placeholder={example}
        aria-invalid={invalid || undefined}
        onBlur={onBlur}
        onChange={(e) => onChange(formatNational(e.target.value.replace(/\D/g, ""), country))}
        className="min-w-0 flex-1 bg-transparent px-3 text-sm text-white outline-none placeholder:text-white/20"
      />
    </div>
  );
}
